import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, Grid, MenuItem, CircularProgress,
  Switch, FormControlLabel, Divider, Chip, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Collapse, IconButton, Tabs, Tab,
} from '@mui/material';
import {
  Save, Business, AccountBalance, LocationOn, AutoMode, PlayArrow, ExpandMore, ExpandLess,
  CheckCircle, Error as ErrorIcon, Schedule, Payment, AccessTime, Security, HelpOutline, InfoOutlined, Extension, Translate,
} from '@mui/icons-material';
import {
  getTenantSettings, updateTenantSettings,
  runAutoInvoice, getAutoInvoiceLog,
  runAutoPayroll, getAutoPayrollLog,
  getPayrollAutomationStatus,
  getSSOSettings, updateSSOSettings,
  getPipelineConfig, updatePipelineConfig,
  getModuleSettings, updateModuleSettings,
  updateTerminology,
} from '../api';
import { fractionToPercentText, percentTextToFraction, WAGE_BASE_RE } from '../utils/unemploymentRate';
import { usePipeline } from '../context/PipelineContext';
import { useModules } from '../context/ModulesContext';
import { useFeatures } from '../context/FeaturesContext';
import { useTerms } from '../context/TerminologyContext';
import { TERM_KEYS, DEFAULT_TERMS, resolveTerms } from '../utils/terminology';
import { useAuth } from '../context/AuthContext';
import EmailSettings from './EmailSettings';
import ZohoSettings from './ZohoSettings';

const US_STATES = [
  { code: '', label: 'Select State' },
  { code: 'AL', label: 'Alabama' }, { code: 'AK', label: 'Alaska' }, { code: 'AZ', label: 'Arizona' },
  { code: 'AR', label: 'Arkansas' }, { code: 'CA', label: 'California' }, { code: 'CO', label: 'Colorado' },
  { code: 'CT', label: 'Connecticut' }, { code: 'DE', label: 'Delaware' }, { code: 'FL', label: 'Florida' },
  { code: 'GA', label: 'Georgia' }, { code: 'HI', label: 'Hawaii' }, { code: 'ID', label: 'Idaho' },
  { code: 'IL', label: 'Illinois' }, { code: 'IN', label: 'Indiana' }, { code: 'IA', label: 'Iowa' },
  { code: 'KS', label: 'Kansas' }, { code: 'KY', label: 'Kentucky' }, { code: 'LA', label: 'Louisiana' },
  { code: 'ME', label: 'Maine' }, { code: 'MD', label: 'Maryland' }, { code: 'MA', label: 'Massachusetts' },
  { code: 'MI', label: 'Michigan' }, { code: 'MN', label: 'Minnesota' }, { code: 'MS', label: 'Mississippi' },
  { code: 'MO', label: 'Missouri' }, { code: 'MT', label: 'Montana' }, { code: 'NE', label: 'Nebraska' },
  { code: 'NV', label: 'Nevada' }, { code: 'NH', label: 'New Hampshire' }, { code: 'NJ', label: 'New Jersey' },
  { code: 'NM', label: 'New Mexico' }, { code: 'NY', label: 'New York' }, { code: 'NC', label: 'North Carolina' },
  { code: 'ND', label: 'North Dakota' }, { code: 'OH', label: 'Ohio' }, { code: 'OK', label: 'Oklahoma' },
  { code: 'OR', label: 'Oregon' }, { code: 'PA', label: 'Pennsylvania' }, { code: 'RI', label: 'Rhode Island' },
  { code: 'SC', label: 'South Carolina' }, { code: 'SD', label: 'South Dakota' }, { code: 'TN', label: 'Tennessee' },
  { code: 'TX', label: 'Texas' }, { code: 'UT', label: 'Utah' }, { code: 'VT', label: 'Vermont' },
  { code: 'VA', label: 'Virginia' }, { code: 'WA', label: 'Washington' }, { code: 'WV', label: 'West Virginia' },
  { code: 'WI', label: 'Wisconsin' }, { code: 'WY', label: 'Wyoming' }, { code: 'DC', label: 'District of Columbia' },
];

const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Puerto_Rico', label: 'Atlantic Time (AST)' },
];

const ENTITY_TYPES = [
  { value: '', label: 'Select Entity Type' },
  { value: 'sole_proprietor', label: 'Sole Proprietor' },
  { value: 'llc', label: 'LLC' },
  { value: 'llc_s_election', label: 'LLC (S-Corp Election)' },
  { value: 's_corp', label: 'S-Corporation' },
  { value: 'c_corp', label: 'C-Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'non_profit', label: 'Non-Profit (501(c)(3))' },
  { value: 'government', label: 'Government Entity' },
];

// Shape the resolved /tenant-settings/modules object into the Modules card's
// local form state (each module defaults ON, home screen defaults full).
const toModuleForm = (d) => ({
  benefits: d?.modules?.benefits !== false,
  time_off: d?.modules?.time_off !== false,
  payroll: d?.modules?.payroll !== false,
  employee_home: d?.employee_home === 'clock' ? 'clock' : 'full',
});

export default function CompanySettings() {
  const [form, setForm] = useState({
    display_name: '', legal_name: '',
    ein: '', state_tax_id: '', state_unemployment_id: '', tax_entity_type: '',
    company_address: '', company_city: '', company_state: '',
    company_zip: '', company_phone: '',
    suta_rate_pct: '', suta_wage_base: '',
    auto_invoice_enabled: false, auto_invoice_schedule: 'weekly', auto_invoice_day: 1,
    auto_zoho_push: false, auto_zoho_sync: false, auto_zoho_sync_interval: 60,
    timezone: 'America/New_York',
    auto_payroll_enabled: false, auto_payroll_require_approval: true,
    auto_process_on_check_date: false, auto_generate_stubs: true,
    supplemental_tax_method: 'aggregate',
    invoice_line_format: 'standard',
    zoho_invoice_numbering: 'hourtrax',
  });
  const [automationMeta, setAutomationMeta] = useState({
    last_auto_invoice_at: null, last_zoho_sync_at: null, last_auto_payroll_at: null,
  });
  const [loading, setLoading] = useState(true);
  const [profileLabel, setProfileLabel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [success, setSuccess] = useState('');
  const [running, setRunning] = useState(false);
  const [payrollRunning, setPayrollRunning] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [payrollLogOpen, setPayrollLogOpen] = useState(false);
  const [automationLog, setAutomationLog] = useState([]);
  const [payrollLog, setPayrollLog] = useState([]);
  const [lastAutoFailure, setLastAutoFailure] = useState(null);

  // SSO state
  const [sso, setSso] = useState({
    auth_method: 'local',
    oidc_client_id: '',
    oidc_client_secret: '',
    oidc_tenant_id: '',
    oidc_auto_provision: true,
  });
  const [ssoLoading, setSsoLoading] = useState(true);
  const [ssoSaving, setSsoSaving] = useState(false);
  const [ssoError, setSsoError] = useState('');
  const [ssoSuccess, setSsoSuccess] = useState('');
  const [hasClientSecret, setHasClientSecret] = useState(false);

  // Payment pipeline state
  const { hasPermission, updateUser } = useAuth();
  const { pipeline_enabled: pipelineEnabled, refresh: refreshPipelineStatus } = usePipeline();
  const [pipelineSaving, setPipelineSaving] = useState(false);
  const [pipelineError, setPipelineError] = useState('');
  const canConfigurePipeline = hasPermission('can_configure_pipeline');

  // Modules + employee home screen (tenants.settings.modules / employee_home).
  // Each control saves on change with a partial PUT, then refreshes the shared
  // context so the nav updates right away — no Save Company Settings needed.
  const { refresh: refreshModules } = useModules();
  const canConfigureModules = hasPermission('can_configure_settings');
  const [moduleForm, setModuleForm] = useState({ benefits: true, time_off: true, payroll: true, employee_home: 'full' });
  const [moduleSaving, setModuleSaving] = useState(false);
  // Shown inside the Modules card — e.g. the 409 "process or void open
  // payroll runs first" refusal — so it is visible next to the switch.
  const [moduleError, setModuleError] = useState('');
  // Admin-only server warning: Payroll-off PTO accrual has no pay period left to run on.
  const { accrualWarning } = useFeatures();
  // Every control is disabled while a save is in flight, but guard against an
  // out-of-order response anyway so an older server snapshot can never
  // overwrite a newer change.
  const moduleSaveSeq = useRef(0);
  // Text the SUTA fields were loaded/saved with: an untouched field is not validated or sent (server keeps stored value).
  const loadedSuta = useRef({ rate: '', base: '' });

  const saveModuleSetting = async (patch, optimistic) => {
    const previous = moduleForm;
    const seq = ++moduleSaveSeq.current;
    setModuleForm(optimistic);
    setModuleSaving(true);
    setError('');
    setModuleError('');
    setSuccess('');
    try {
      const res = await updateModuleSettings(patch);
      if (seq !== moduleSaveSeq.current) return;
      setModuleForm(toModuleForm(res.data));
      await refreshModules();
      setSuccess('Modules updated');
    } catch (err) {
      if (seq !== moduleSaveSeq.current) return;
      setModuleForm(previous);
      setModuleError(err.response?.data?.error || 'Failed to update modules');
    } finally {
      if (seq === moduleSaveSeq.current) setModuleSaving(false);
    }
  };

  const handleModuleToggle = (key) => (e) => {
    const checked = e.target.checked;
    saveModuleSetting({ modules: { [key]: checked } }, { ...moduleForm, [key]: checked });
  };

  const handleEmployeeHomeChange = (e) => {
    const value = e.target.value;
    saveModuleSetting({ employee_home: value }, { ...moduleForm, employee_home: value });
  };

  const togglePipelineEnabled = async (enabled) => {
    setPipelineSaving(true);
    setPipelineError('');
    try {
      const current = await getPipelineConfig();
      let stages = current.data.config.stages;
      // When enabling the pipeline, if the current config has no stages enabled
      // (e.g. previously cleared), default to the Full preset so the user sees
      // the full workflow they just turned on.
      if (enabled) {
        const anyEnabled = Object.values(stages || {}).some((s) => s?.enabled !== false);
        if (!anyEnabled) {
          stages = {
            approved:      { enabled: true, auto: false },
            invoiced:      { enabled: true, auto: false },
            invoice_paid:  { enabled: true, auto: true  },
            payroll_ready: { enabled: true, auto: true  },
          };
        }
      }
      await updatePipelineConfig({
        pipeline_enabled: enabled,
        stages,
      });
      await refreshPipelineStatus();
      await refreshModules(); // refreshes the feature registry flags too (pipeline)
    } catch (err) {
      setPipelineError(err.response?.data?.error || 'Failed to update pipeline');
    } finally {
      setPipelineSaving(false);
    }
  };

  const loadLog = useCallback(async () => {
    try {
      const res = await getAutoInvoiceLog(20);
      setAutomationLog(res.data);
    } catch { /* ignore */ }
  }, []);

  const loadPayrollLog = useCallback(async () => {
    try {
      const res = await getAutoPayrollLog(20);
      setPayrollLog(res.data);
    } catch { /* ignore */ }
    try {
      const st = await getPayrollAutomationStatus();
      setLastAutoFailure(st.data?.last_failure || null);
    } catch { setLastAutoFailure(null); }
  }, []);

  useEffect(() => {
    if (form.auto_payroll_enabled) loadPayrollLog();
  }, [form.auto_payroll_enabled, loadPayrollLog]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getTenantSettings();
        const d = res.data;
        setProfileLabel(d.profile_label || null);
        loadedSuta.current = {
          rate: fractionToPercentText(d.suta_rate),
          base: d.suta_wage_base != null ? String(Number(d.suta_wage_base)) : '',
        };
        setForm({
          display_name: d.display_name || '',
          legal_name: d.legal_name || '',
          ein: d.ein || '',
          state_tax_id: d.state_tax_id || '',
          state_unemployment_id: d.state_unemployment_id || '',
          tax_entity_type: d.tax_entity_type || '',
          company_address: d.company_address || '',
          company_city: d.company_city || '',
          company_state: d.company_state || '',
          company_zip: d.company_zip || '',
          company_phone: d.company_phone || '',
          suta_rate_pct: fractionToPercentText(d.suta_rate),
          suta_wage_base: d.suta_wage_base != null ? String(Number(d.suta_wage_base)) : '',
          timezone: d.timezone || 'America/New_York',
          auto_invoice_enabled: d.auto_invoice_enabled || false,
          auto_invoice_schedule: d.auto_invoice_schedule || 'weekly',
          auto_invoice_day: d.auto_invoice_day || 1,
          auto_zoho_push: d.auto_zoho_push || false,
          auto_zoho_sync: d.auto_zoho_sync || false,
          auto_zoho_sync_interval: d.auto_zoho_sync_interval || 60,
          auto_payroll_enabled: d.auto_payroll_enabled || false,
          auto_payroll_require_approval: d.auto_payroll_require_approval !== false,
          auto_process_on_check_date: d.auto_process_on_check_date || false,
          auto_generate_stubs: d.auto_generate_stubs !== false,
          supplemental_tax_method: d.supplemental_tax_method || 'aggregate',
          invoice_line_format: d.invoice_line_format || 'standard',
          zoho_invoice_numbering: d.zoho_invoice_numbering || 'hourtrax',
        });
        setAutomationMeta({
          last_auto_invoice_at: d.last_auto_invoice_at,
          last_zoho_sync_at: d.last_zoho_sync_at,
          last_auto_payroll_at: d.last_auto_payroll_at,
        });
      } catch (err) {
        setError('Failed to load company settings');
      } finally {
        setLoading(false);
      }

      // Load SSO settings
      try {
        const ssoRes = await getSSOSettings();
        const s = ssoRes.data;
        setSso({
          auth_method: s.auth_method || 'local',
          oidc_client_id: s.oidc_client_id || '',
          oidc_client_secret: '',
          oidc_tenant_id: s.oidc_tenant_id || '',
          oidc_auto_provision: s.oidc_auto_provision !== false,
        });
        setHasClientSecret(s.oidc_client_secret_set || false);
      } catch (err) {
        console.error('Failed to load SSO settings:', err);
      } finally {
        setSsoLoading(false);
      }

      // Load module switches + employee home screen
      try {
        const modRes = await getModuleSettings();
        setModuleForm(toModuleForm(modRes.data));
      } catch (err) {
        console.error('Failed to load module settings:', err);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    const rateEdited = form.suta_rate_pct !== loadedSuta.current.rate;
    const baseEdited = form.suta_wage_base !== loadedSuta.current.base;
    const frac = percentTextToFraction(form.suta_rate_pct);
    if (rateEdited && (form.suta_rate_pct.trim() === '' || frac == null)) {
      setError(form.suta_rate_pct.trim() === ''
        ? 'SUTA Rate (%) is required when you edit it; enter the rate from your UC-657 notice, e.g. 3.822'
        : 'SUTA Rate (%) must be a percentage above 0 and up to 15 with at most 4 decimals, e.g. 3.822');
      return;
    }
    const baseText = form.suta_wage_base.trim();
    if (baseEdited && (!WAGE_BASE_RE.test(baseText) || Number(baseText) <= 0)) {
      setError('SUTA Wage Base must be a dollar amount above 0, e.g. 10000');
      return;
    }
    setSaving(true);
    try {
      // Ensure numeric fields are sent as numbers (#10)
      const { suta_rate_pct, suta_wage_base: _unusedBase, ...rest } = form;
      const payload = {
        ...rest,
        ...(rateEdited ? { suta_rate: frac } : {}),
        ...(baseEdited ? { suta_wage_base: baseText } : {}),
        auto_invoice_day: parseInt(form.auto_invoice_day) || 1,
        auto_zoho_sync_interval: parseInt(form.auto_zoho_sync_interval) || 60,
      };
      const res = await updateTenantSettings(payload);
      if (res && res.data) {
        loadedSuta.current = {
          rate: fractionToPercentText(res.data.suta_rate),
          base: res.data.suta_wage_base != null ? String(Number(res.data.suta_wage_base)) : '',
        };
        setForm(f => ({
          ...f,
          suta_rate_pct: fractionToPercentText(res.data.suta_rate),
          suta_wage_base: res.data.suta_wage_base != null ? String(Number(res.data.suta_wage_base)) : f.suta_wage_base,
        }));
      }
      updateUser({ tenantTimezone: payload.timezone || null });
      setSuccess('Company settings saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const handleToggle = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.checked }));
  };

  const handleRunNow = async () => {
    setRunning(true);
    setError('');
    try {
      const res = await runAutoInvoice();
      const { invoicesCreated, entriesInvoiced } = res.data;
      if (invoicesCreated > 0) {
        setSuccess(`Created ${invoicesCreated} invoice(s) from ${entriesInvoiced} approved entries`);
      } else {
        setSuccess('No approved entries to invoice');
      }
      loadLog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to run auto-invoice');
    } finally {
      setRunning(false);
    }
  };

  const handlePayrollRunNow = async () => {
    setPayrollRunning(true);
    setError('');
    try {
      const res = await runAutoPayroll();
      const { runsCreated, periodsChecked, awaitingCheckDate } = res.data;
      if (runsCreated > 0) {
        setSuccess(`Created ${runsCreated} payroll run(s) from ${periodsChecked} eligible period(s).${awaitingCheckDate > 0 ? ` ${awaitingCheckDate} run(s) have no Paycheck Date and wait in review: set it on the Payroll tab, then approve.` : ''}`);
      } else {
        setSuccess(`No eligible pay periods found (checked ${periodsChecked || 0})`);
      }
      loadPayrollLog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to run auto-payroll');
    } finally {
      setPayrollRunning(false);
    }
  };

  const handleSsoChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSso(s => ({ ...s, [field]: value }));
  };

  const handleSsoSave = async () => {
    setSsoSaving(true);
    setSsoError('');
    setSsoSuccess('');
    try {
      const res = await updateSSOSettings(sso);
      setSsoSuccess('SSO settings saved');
      setHasClientSecret(res.data.oidc_client_secret_set || false);
      if (sso.oidc_client_secret) {
        setSso(s => ({ ...s, oidc_client_secret: '' }));
      }
    } catch (err) {
      setSsoError(err.response?.data?.error || 'Failed to save SSO settings');
    } finally {
      setSsoSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>Company Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This information appears on pay stubs, W-2s, tax filings, and official documents.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, maxWidth: 750 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="General" icon={<Business />} iconPosition="start" />
          <Tab label="Automation" icon={<AutoMode />} iconPosition="start" />
          <Tab label="Integrations" icon={<Security />} iconPosition="start" />
        </Tabs>
      </Box>

      {activeTab === 0 && (<>
      <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Business sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Company Identity</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Legal Business Name" value={form.legal_name}
              onChange={handleChange('legal_name')}
              placeholder="Acme Corporation, Inc."
              helperText="Name as registered with the IRS — appears on W-2s and tax forms" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Display Name (DBA)" value={form.display_name}
              onChange={handleChange('display_name')}
              placeholder="Acme Corp"
              helperText="Trade name shown in the app and on pay stubs" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Entity Type" value={form.tax_entity_type}
              onChange={handleChange('tax_entity_type')}>
              {ENTITY_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Tax Identification</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="EIN" value={form.ein}
              onChange={handleChange('ein')}
              placeholder="XX-XXXXXXX"
              helperText="Federal Employer Identification Number"
              inputProps={{ maxLength: 20 }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <TextField fullWidth label="State Tax ID" value={form.state_tax_id}
                onChange={handleChange('state_tax_id')}
                helperText="Pennsylvania withholding account number (used by the PA-W3, REV-1667 and W-2 Box 15 for PA employees)"
                inputProps={{ maxLength: 50 }} />
              <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="Pennsylvania withholding account number (used by the PA-W3, REV-1667 and W-2 Box 15 for PA employees). It prints on those forms exactly as you enter it here.">
                <InfoOutlined fontSize="small" color="action" sx={{ mt: 2 }} />
              </Tooltip>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="State Unemployment ID (SUI)" value={form.state_unemployment_id}
              onChange={handleChange('state_unemployment_id')}
              helperText="SUTA / SUI account number"
              inputProps={{ maxLength: 50 }} />
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 1 }}>
          Employer unemployment contribution (SUTA)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <TextField fullWidth label="SUTA Rate (%)" value={form.suta_rate_pct} disabled={saving}
                onChange={handleChange('suta_rate_pct')}
                error={form.suta_rate_pct.trim() !== '' && percentTextToFraction(form.suta_rate_pct) == null}
                helperText={percentTextToFraction(form.suta_rate_pct)
                  ? `Stored as ${percentTextToFraction(form.suta_rate_pct)}`
                  : 'Percent from your UC-657 rate notice, e.g. 3.822'}
                inputProps={{ inputMode: 'decimal', maxLength: 7 }} />
              <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="Your employer contribution rate for unemployment compensation, from your state's annual rate notice (Pennsylvania: Form UC-657, issued for each calendar year; also update it if L&I sends a revised notice). Enter the total rate as a percentage, for example 3.822. Change it after the last payroll of the old year is calculated and before the first payroll of the new year. HourTrax uses it for the employer unemployment tax on every paycheck and for item 5 of the Pennsylvania UC-2 worksheet. A new rate applies to payroll runs calculated after you save; recalculate any open run.">
                <InfoOutlined fontSize="small" color="action" sx={{ mt: 2 }} />
              </Tooltip>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <TextField fullWidth label="SUTA Wage Base ($)" value={form.suta_wage_base} disabled={saving}
                onChange={handleChange('suta_wage_base')}
                helperText="Taxable wages per employee per year. Pennsylvania: $10,000"
                inputProps={{ inputMode: 'decimal', maxLength: 10 }} />
              <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="The amount of each employee's yearly pay that is subject to employer unemployment tax. Pennsylvania is $10,000; other states differ. The UC-2 worksheet warns when this differs from Pennsylvania's figure.">
                <InfoOutlined fontSize="small" color="action" sx={{ mt: 2 }} />
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LocationOn sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Address & Contact</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Street Address" value={form.company_address}
              onChange={handleChange('company_address')}
              placeholder="123 Main St, Suite 100" />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField fullWidth label="City" value={form.company_city}
              onChange={handleChange('company_city')} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth select label="State" value={form.company_state}
              onChange={handleChange('company_state')}>
              {US_STATES.map(s => (
                <MenuItem key={s.code} value={s.code}>{s.code ? `${s.code} — ${s.label}` : s.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth label="ZIP Code" value={form.company_zip}
              onChange={handleChange('company_zip')}
              inputProps={{ maxLength: 10 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Phone" value={form.company_phone}
              onChange={handleChange('company_phone')}
              placeholder="(555) 123-4567" />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AccessTime sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Regional Settings</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Timezone" value={form.timezone}
              onChange={handleChange('timezone')}
              helperText="Default for everyone whose My Profile timezone is Company default. Used for dashboard dates, clock-in times and scheduling.">
              {US_TIMEZONES.map(tz => (
                <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Modules + employee home screen — saves on change, not via the Save button */}
      {canConfigureModules && (
      <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Extension sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Modules</Typography>
          <Tooltip title="Turn whole features on or off for this organization. A module that is off disappears from everyone's menu and its pages stop accepting new requests. Contractors (1099) never see Time Off, My Pay or My Benefits regardless of these switches. Each change saves immediately — the Save Company Settings button is not needed.">
            <HelpOutline sx={{ ml: 1, fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Hide the features your organization doesn't use. Changes apply as soon as you flip them.
        </Typography>
        {profileLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Started from: <strong>{profileLabel}</strong> profile
            </Typography>
            <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="The starting template HourTrax used when your organization was set up. It set these switches and a few defaults once; it is only a record now. Every setting on this page can be changed on its own.">
              <IconButton size="small" sx={{ ml: 0.5 }} aria-label="About the starting profile"><HelpOutline fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
        )}
        {moduleError && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setModuleError('')}>{moduleError}</Alert>
        )}
        {accrualWarning && (
          <Alert severity="warning" sx={{ mb: 1, py: 0, fontSize: '0.8125rem' }}>
            {/* One issue per line from the server — a short list when there are several */}
            {accrualWarning.includes('\n') ? (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {accrualWarning.split('\n').map((line) => <li key={line}>{line}</li>)}
              </Box>
            ) : accrualWarning}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={<Switch checked={moduleForm.benefits} onChange={handleModuleToggle('benefits')} disabled={moduleSaving} />}
            label="Benefits"
          />
          <Tooltip title="Benefit plans, enrollment periods and the My Benefits page.">
            <IconButton size="small"><HelpOutline fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={<Switch checked={moduleForm.time_off} onChange={handleModuleToggle('time_off')} disabled={moduleSaving} />}
            label="Time off"
          />
          <Tooltip title="Time-off requests, PTO balances and PTO approvals. Turning this off stops new requests and hides balances; accrual keeps running in the background so the record stays complete. You can't turn it off while time-off requests are pending — approve, deny or cancel them first.">
            <IconButton size="small"><HelpOutline fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={<Switch checked={moduleForm.payroll} onChange={handleModuleToggle('payroll')} disabled={moduleSaving} />}
            label="Payroll"
          />
          <Tooltip title="Payroll runs, deductions, tax filing, pay stubs and the My Pay page. Turn off if you pay people outside HourTrax (for example contractors paid by check). Records already created stay in HourTrax. Employers must still keep payroll records for 3 years and give each employee a wage statement each pay period. PTO keeps accruing either way: with Payroll off, HourTrax credits each employee's accrual rate when each pay period on the Pay Periods tab ends. Only employees set to per-pay-period accrue automatically while Payroll is off. You can't turn Payroll off while a payroll run is still open — process or void it first.">
            <IconButton size="small"><HelpOutline fontSize="small" /></IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField fullWidth select label="Employee home screen" value={moduleForm.employee_home}
                onChange={handleEmployeeHomeChange} size="small" disabled={moduleSaving}
                InputLabelProps={{ shrink: true }}>
                <MenuItem value="full">Full dashboard</MenuItem>
                <MenuItem value="clock">Clock only</MenuItem>
              </TextField>
              <Tooltip title="Clock only shows employees just the time clock; the manual entry form sits behind a Missed a punch? Add time button. Managers and admins keep the full dashboard.">
                <IconButton size="small" sx={{ ml: 0.5 }}><HelpOutline fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      )}

      {/* Terminology — per-tenant words for Client / Affiliate / Department / Property */}
      {canConfigureModules && (
        <TerminologyCard onError={setError} onSuccess={setSuccess} />
      )}
      </>)}

      {activeTab === 1 && (<>
      {/* Payment Pipeline Toggle */}
      {canConfigurePipeline && (
        <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Schedule sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Payment Pipeline</Typography>
            <Tooltip title="The payment pipeline adds optional workflow stages between clocking in and paying employees: approval gates, invoice tracking, and payroll-ready queuing. Most small companies don't need this — leave it off for a simple pending → paid flow.">
              <IconButton size="small" sx={{ ml: 0.5 }}><HelpOutline fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
          {pipelineError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPipelineError('')}>{pipelineError}</Alert>}
          <FormControlLabel
            control={<Switch
              checked={pipelineEnabled}
              onChange={(e) => togglePipelineEnabled(e.target.checked)}
              disabled={pipelineSaving}
            />}
            label={pipelineEnabled ? 'Pipeline enabled' : 'Pipeline disabled — simple direct pay (pending → paid)'}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 1 }}>
            {pipelineEnabled
              ? 'Open the Pipeline tab in Finance to configure which stages (approval, invoicing, payroll-ready) are active.'
              : 'Time entries go directly from pending to paid. Enable the pipeline to add approval gates or invoice tracking to the workflow.'}
          </Typography>
        </Paper>
      )}

      <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AutoMode sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Invoice Automation</Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={8}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField fullWidth select label="Invoice Line Format" value={form.invoice_line_format}
                onChange={handleChange('invoice_line_format')} size="small"
                InputLabelProps={{ shrink: true }}>
                <MenuItem value="standard">Affiliate - Description (default)</MenuItem>
                <MenuItem value="date_details">Date - Description + Work Details</MenuItem>
                <MenuItem value="date_description">Date - Description</MenuItem>
              </TextField>
              <Tooltip title="How each invoice line item is written when invoices are built from time entries. Standard shows the affiliate and the client-facing Description. Date - Description + Work Details starts each line with the entry date (e.g. 6/2/2026 - ...) and includes the entry's Work Details below it — note this makes Work Details visible to the client on invoices and pushed Zoho invoices. Date - Description is the same dated line without Work Details (one short line per entry). On Zoho pushes, dated lines use 'date - affiliate (or client)' as the item name and the rest of the line as the item description. Applies to newly generated invoices and to Recalculate on existing drafts.">
                <IconButton size="small" sx={{ ml: 0.5 }}><HelpOutline fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={<Switch checked={form.auto_invoice_enabled} onChange={handleToggle('auto_invoice_enabled')} />}
          label="Enable automatic invoice creation"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 2 }}>
          Automatically groups approved time entries by company and creates draft invoices on schedule
        </Typography>

        {form.auto_invoice_enabled && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Schedule" value={form.auto_invoice_schedule}
                onChange={handleChange('auto_invoice_schedule')} size="small">
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="biweekly">Every 2 Weeks</MenuItem>
                <MenuItem value="semimonthly">Semi-Monthly (1st & 16th)</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="on_demand">Manual Only</MenuItem>
              </TextField>
            </Grid>
            {(form.auto_invoice_schedule === 'weekly' || form.auto_invoice_schedule === 'biweekly') && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Day of Week" value={form.auto_invoice_day}
                  onChange={handleChange('auto_invoice_day')} size="small">
                  <MenuItem value={0}>Sunday</MenuItem>
                  <MenuItem value={1}>Monday</MenuItem>
                  <MenuItem value={2}>Tuesday</MenuItem>
                  <MenuItem value={3}>Wednesday</MenuItem>
                  <MenuItem value={4}>Thursday</MenuItem>
                  <MenuItem value={5}>Friday</MenuItem>
                  <MenuItem value={6}>Saturday</MenuItem>
                </TextField>
              </Grid>
            )}
            {form.auto_invoice_schedule === 'monthly' && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label="Day of Month" value={form.auto_invoice_day}
                  onChange={handleChange('auto_invoice_day')} size="small"
                  inputProps={{ min: 1, max: 28 }}
                  helperText="1-28" />
              </Grid>
            )}
          </Grid>
        )}

        {form.auto_invoice_enabled && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button variant="outlined" size="small" startIcon={running ? <CircularProgress size={16} /> : <PlayArrow />}
              onClick={handleRunNow} disabled={running}>
              Run Now
            </Button>
            {automationMeta.last_auto_invoice_at && (
              <Typography variant="caption" color="text.secondary">
                Last run: {new Date(automationMeta.last_auto_invoice_at).toLocaleString()}
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Zoho Integration Automation</Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={8}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField fullWidth select label="Invoice Numbers on Zoho Push" value={form.zoho_invoice_numbering}
                onChange={handleChange('zoho_invoice_numbering')} size="small"
                InputLabelProps={{ shrink: true }}>
                <MenuItem value="hourtrax">HourTrax assigns (default)</MenuItem>
                <MenuItem value="zoho">Zoho assigns on first push</MenuItem>
              </TextField>
              <Tooltip title="Who owns invoice numbering when pushing to Zoho Books. HourTrax assigns: the HourTrax number (e.g. INV-2026-00001) is forced onto the Zoho invoice. Zoho assigns: on the FIRST push Zoho gives the invoice the next number in your Zoho org's own sequence (e.g. INV-000112) and HourTrax renames the invoice to match, so your books stay one continuous series. The number changes at first push — push to Zoho BEFORE sending the invoice to the client. Requires auto-numbering enabled in the Zoho org.">
                <IconButton size="small" sx={{ ml: 0.5 }}><HelpOutline fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        <FormControlLabel
          control={<Switch checked={form.auto_zoho_push} onChange={handleToggle('auto_zoho_push')} />}
          label="Auto-push new invoices to Zoho"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 1 }}>
          Automatically syncs each new auto-invoice to Zoho Invoice (requires active Zoho connection)
        </Typography>

        <FormControlLabel
          control={<Switch checked={form.auto_zoho_sync} onChange={handleToggle('auto_zoho_sync')} />}
          label="Auto-sync payment status from Zoho"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 1 }}>
          Periodically checks Zoho for paid invoices and cascades to payroll-ready status
        </Typography>

        {form.auto_zoho_sync && (
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Sync Interval" value={form.auto_zoho_sync_interval}
                onChange={handleChange('auto_zoho_sync_interval')} size="small">
                <MenuItem value={15}>Every 15 minutes</MenuItem>
                <MenuItem value={30}>Every 30 minutes</MenuItem>
                <MenuItem value={60}>Every hour</MenuItem>
                <MenuItem value={240}>Every 4 hours</MenuItem>
                <MenuItem value={1440}>Once daily</MenuItem>
              </TextField>
            </Grid>
            {automationMeta.last_zoho_sync_at && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Last sync: {new Date(automationMeta.last_zoho_sync_at).toLocaleString()}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => { setLogOpen(!logOpen); if (!logOpen) loadLog(); }}>
          <Typography variant="subtitle2">Automation Log</Typography>
          <IconButton size="small">{logOpen ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </Box>

        <Collapse in={logOpen}>
          {automationLog.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No automation runs yet</Typography>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {automationLog.map(row => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(row.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip label={row.action.replace(/_/g, ' ')} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {row.status === 'success' ? (
                        <Tooltip title="Success"><CheckCircle color="success" fontSize="small" /></Tooltip>
                      ) : (
                        <Tooltip title="Error"><ErrorIcon color="error" fontSize="small" /></Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.details?.invoicesCreated != null
                        ? `${row.details.invoicesCreated} invoices, ${row.details.entriesInvoiced} entries`
                        : row.details?.checked != null
                        ? `Checked ${row.details.checked}, updated ${row.details.updated}`
                        : row.details?.error || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Collapse>
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Payment sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Payroll Automation</Typography>
        </Box>

        <FormControlLabel
          control={<Switch checked={form.auto_payroll_enabled} onChange={handleToggle('auto_payroll_enabled')} />}
          label="Enable automatic payroll processing"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 2 }}>
          Automatically creates and calculates payroll runs when pay periods are locked with payroll-ready entries. An automatic run has no Paycheck Date, so it waits in review until someone sets its Paycheck Date and approves it on the Payroll tab.
        </Typography>

        {form.auto_payroll_enabled && (
          <>
            <FormControlLabel
              control={<Switch checked={form.auto_payroll_require_approval} onChange={handleToggle('auto_payroll_require_approval')} />}
              label="Require manager email approval"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 1 }}>
              Sends approval emails to managers/admins with approve/reject links. No email is sent for a run without a Paycheck Date, and automatic runs are created without one, so approve them on the Payroll tab.
            </Typography>

            <FormControlLabel
              control={<Switch checked={form.auto_process_on_check_date} onChange={handleToggle('auto_process_on_check_date')} />}
              label="Auto-process on check date"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 1 }}>
              Automatically processes approved payroll runs when the check date arrives
            </Typography>

            <FormControlLabel
              control={<Switch checked={form.auto_generate_stubs} onChange={handleToggle('auto_generate_stubs')} />}
              label="Auto-generate pay stubs"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 2 }}>
              Automatically generates pay stub PDFs when payroll is processed
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Button variant="outlined" size="small" startIcon={payrollRunning ? <CircularProgress size={16} /> : <PlayArrow />}
                onClick={handlePayrollRunNow} disabled={payrollRunning}>
                Run Now
              </Button>
              {automationMeta.last_auto_payroll_at && (
                <Typography variant="caption" color="text.secondary">
                  Last run: {new Date(automationMeta.last_auto_payroll_at).toLocaleString()}
                </Typography>
              )}
            </Box>

            {lastAutoFailure && (
              <Alert severity="warning" sx={{ mb: 2 }}
                icon={<Tooltip title={lastAutoFailure.message}><InfoOutlined fontSize="inherit" /></Tooltip>}>
                Last automatic run problem: run #{lastAutoFailure.run_id} ({lastAutoFailure.period_name}, check date {lastAutoFailure.check_date}) was not processed - {lastAutoFailure.message}. {lastAutoFailure.recovery}
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => { setPayrollLogOpen(!payrollLogOpen); if (!payrollLogOpen) loadPayrollLog(); }}>
              <Typography variant="subtitle2">Payroll Automation Log</Typography>
              <IconButton size="small">{payrollLogOpen ? <ExpandLess /> : <ExpandMore />}</IconButton>
            </Box>

            <Collapse in={payrollLogOpen}>
              {payrollLog.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No payroll automation runs yet</Typography>
              ) : (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payrollLog.map(row => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(row.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip label={row.action.replace(/_/g, ' ')} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {row.status === 'success' ? (
                            <Tooltip title="Success"><CheckCircle color="success" fontSize="small" /></Tooltip>
                          ) : row.status === 'partial' ? (
                            <Tooltip title="Partial"><Schedule color="warning" fontSize="small" /></Tooltip>
                          ) : (
                            <Tooltip title="Error"><ErrorIcon color="error" fontSize="small" /></Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.action === 'auto_process_notice'
                            ? `Notice sent to ${row.details?.recipients ?? 0} payroll user(s) (${row.details?.reason_code || 'error'})`
                            : row.details?.runsCreated != null
                            ? `${row.details.runsCreated} runs from ${row.details.periodsChecked} periods`
                            : row.details?.awaitingCheckDate
                            ? `${row.details.periodName} (waiting for a Paycheck Date)`
                            : row.details?.periodName
                            ? `${row.details.periodName}${row.details.requireApproval ? ' (approval sent)' : ' (auto-approved)'}`
                            : row.details?.checkDate
                            ? `Processed (check: ${row.details.checkDate})`
                            : row.details?.error || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Collapse>
          </>
        )}
      </Paper>

      {/* Supplemental Tax Method — how bonuses/OT are federally withheld */}
      <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Payment sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Supplemental Wage Withholding</Typography>
          <Tooltip title="How federal income tax is withheld from bonuses, commissions, and overtime pay (IRS Pub 15 Section 7). Most employers use Aggregate — it matches the employee's actual tax bracket. Use Flat 22% only for standalone bonus payroll runs. The mandatory 37% rate for YTD supplemental over $1M always applies regardless of choice.">
            <HelpOutline sx={{ ml: 1, fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <TextField
          select
          fullWidth
          size="small"
          label="Method for supplemental wages (bonuses, OT, commissions)"
          value={form.supplemental_tax_method}
          onChange={handleChange('supplemental_tax_method')}
          sx={{ mt: 1 }}
        >
          <MenuItem value="aggregate">
            Aggregate — combine with regular wages (recommended, matches Gusto/QuickBooks)
          </MenuItem>
          <MenuItem value="flat">
            Flat 22% — withhold supplemental wages at flat rate (simpler, over-withholds at lower incomes)
          </MenuItem>
        </TextField>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {form.supplemental_tax_method === 'aggregate'
            ? 'Bonuses and overtime are added to regular wages and run through the W-4 withholding brackets once. This matches the employee\'s actual marginal tax rate and results in accurate paycheck withholding.'
            : 'Bonuses and overtime are withheld separately at a flat 22% federal rate. This is allowed by IRS Pub 15 Section 7 but often over-withholds for employees under ~$250k/year.'}
        </Typography>
      </Paper>
      </>)}

      {activeTab === 2 && (<>
      {/* SSO / Entra ID Configuration */}
      <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Security sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Single Sign-On (SSO)</Typography>
          <Tooltip title="Connect to Microsoft Entra ID (Azure AD) to let employees sign in with their corporate Microsoft account. Requires an App Registration in your Azure tenant.">
            <HelpOutline sx={{ ml: 1, fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enable Microsoft Entra ID login for your organization. Users can sign in with their Microsoft account instead of a password.
        </Typography>

        {ssoError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSsoError('')}>{ssoError}</Alert>}
        {ssoSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSsoSuccess('')}>{ssoSuccess}</Alert>}

        {ssoLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={sso.auth_method === 'oidc'}
                  onChange={(e) => setSso(s => ({ ...s, auth_method: e.target.checked ? 'oidc' : 'local' }))}
                />
              }
              label="Enable Entra ID Single Sign-On"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 2 }}>
              When enabled, users see a "Sign in with Microsoft" button on the login page. Password login remains available as fallback.
            </Typography>

            {sso.auth_method === 'oidc' && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Azure App Registration Setup:</Typography>
                  <ol style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Go to <strong>Azure Portal</strong> &rarr; <strong>Entra ID</strong> &rarr; App Registrations &rarr; <strong>New Registration</strong></li>
                    <li>Name: <strong>HourTrax SSO</strong></li>
                    <li>Supported account types: <strong>Accounts in this organizational directory only (Single tenant)</strong></li>
                    <li>Redirect URI: select platform <strong>Web</strong>, enter: <strong>{window.location.origin}/api/auth/oidc/callback</strong></li>
                    <li>Click <strong>Register</strong></li>
                    <li>Go to <strong>Certificates &amp; Secrets</strong> &rarr; <strong>New client secret</strong> &rarr; copy the <strong>Value</strong> (not the Secret ID)</li>
                    <li>Go to <strong>API Permissions</strong> &rarr; verify <strong>Microsoft Graph &rarr; Delegated permissions</strong>: <strong>openid</strong>, <strong>profile</strong>, <strong>email</strong> are listed (these are added by default)</li>
                    <li>Copy the <strong>Application (Client) ID</strong> and <strong>Directory (Tenant) ID</strong> from the app's Overview page, and paste below along with the Client Secret</li>
                  </ol>
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Tooltip title="The Application (Client) ID from your Azure App Registration. Found on the app's Overview page.">
                      <TextField fullWidth label="Application (Client) ID" value={sso.oidc_client_id}
                        onChange={handleSsoChange('oidc_client_id')}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        helperText="From Azure App Registration > Overview" />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Tooltip title="Your Azure AD Directory (Tenant) ID. Found in Azure Portal > Entra ID > Overview.">
                      <TextField fullWidth label="Directory (Tenant) ID" value={sso.oidc_tenant_id}
                        onChange={handleSsoChange('oidc_tenant_id')}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        helperText="From Entra ID > Overview" />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Tooltip title="Client Secret value from your App Registration. Only needs to be entered once — it's stored securely and not displayed again.">
                      <TextField fullWidth type="password" label="Client Secret" value={sso.oidc_client_secret}
                        onChange={handleSsoChange('oidc_client_secret')}
                        placeholder={hasClientSecret ? '••••••••' : 'Enter client secret'}
                        helperText={hasClientSecret ? 'Leave blank to keep existing secret' : 'From Certificates & Secrets'} />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ pt: 1 }}>
                      <Tooltip title="When enabled, users who sign in via Microsoft for the first time are automatically created as employees with the 'user' role. Disable to require manual account creation first.">
                        <FormControlLabel
                          control={
                            <Switch checked={sso.oidc_auto_provision} onChange={handleSsoChange('oidc_auto_provision')} />
                          }
                          label="Auto-provision new users"
                        />
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                        Automatically create accounts for new SSO users
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Redirect URI</strong> (add this in Azure): <code>{window.location.origin}/api/auth/oidc/callback</code>
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}

            <Box sx={{ mt: 2 }}>
              <Button variant="contained" size="small"
                startIcon={ssoSaving ? <CircularProgress size={16} /> : <Save />}
                onClick={handleSsoSave} disabled={ssoSaving}>
                Save SSO Settings
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Email Configuration */}
      <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
        <EmailSettings embedded />
      </Paper>

      {/* Zoho Integration */}
      <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
        <ZohoSettings embedded />
      </Paper>
      </>)}

      {/* Save button only for tabs that use the main form (General + Automation) */}
      {activeTab < 2 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', maxWidth: 750 }}>
          <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={18} /> : <Save />}
            onClick={handleSave} disabled={saving}>
            Save Company Settings
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ── Terminology card ─────────────────────────────────────────────────
// Saves tenants.settings.terminology via PUT /tenant-settings/terminology.
// Gated by the same can_configure_settings flag as Modules (see the
// 'terminology' how-to section in OperationalDocs.js).
const TERM_ROWS = [
  { key: 'client', caption: 'Clients (who you bill)', info: 'The organizations you bill for hours — the Clients screen, client pickers on time entries, invoices and the Timesheets column.' },
  { key: 'affiliate', caption: 'Affiliates (client sub-locations)', info: 'Sub-organizations or sites under one client — the Affiliates screen and the Affiliate picker on time entries.' },
  { key: 'department', caption: 'Departments', info: 'Teams inside your organization — the Departments screen and Department pickers.' },
  { key: 'location', caption: 'Clock-in place', info: 'What crews pick under Where on the clock and what Switch … and Tasks call it.' },
];

const termsToForm = (terms) => {
  const f = {};
  TERM_KEYS.forEach((k) => { f[k] = { singular: terms[k].singular, plural: terms[k].plural }; });
  return f;
};

function TerminologyCard({ onError, onSuccess }) {
  const { terms, refresh } = useTerms();
  const [form, setForm] = useState(() => termsToForm(terms));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-sync from the context unless the admin has unsaved edits.
  useEffect(() => {
    if (!dirty) setForm(termsToForm(terms));
  }, [terms, dirty]);

  const handleChange = (key, field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    setDirty(true);
  };

  const handleReset = () => {
    setForm(termsToForm(DEFAULT_TERMS));
    setDirty(true);
  };

  const handleSave = async () => {
    onError('');
    onSuccess('');
    const payload = {};
    for (const key of TERM_KEYS) {
      const singular = form[key].singular.trim();
      const plural = form[key].plural.trim();
      if (!singular && !plural) {
        payload[key] = null;
      } else if (!singular || !plural) {
        onError('Enter both the singular and plural, or clear both to use the default');
        return;
      } else {
        payload[key] = { singular, plural };
      }
    }
    setSaving(true);
    try {
      const res = await updateTerminology(payload);
      setForm(termsToForm(resolveTerms(res.data && res.data.terminology)));
      // Keep the saved values on screen until the context has the new map, so
      // the inputs never flicker back; on a refresh failure they stay as saved.
      try { await refresh(); } catch (e) { /* saved; the context keeps its last map */ }
      setDirty(false);
      onSuccess('Terminology updated');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to update terminology');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 750, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Translate sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Terminology</Typography>
        <Tooltip title="Rename the words HourTrax uses for your clients, their sub-locations, departments and the place crews clock in, so screens match how your team talks. Only the labels change — your data, invoices and reports stay the same. Saved with the Save terminology button below.">
          <HelpOutline sx={{ ml: 1, fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Change what these are called across HourTrax for everyone in your organization.
      </Typography>

      <Grid container spacing={2} alignItems="center">
        {TERM_ROWS.map((row) => (
          <React.Fragment key={row.key}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">{row.caption}</Typography>
                <Tooltip title={row.info}>
                  <IconButton size="small" sx={{ ml: 0.5 }} aria-label={`About ${row.caption}`}><HelpOutline fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="Singular" value={form[row.key].singular}
                onChange={handleChange(row.key, 'singular')} placeholder={DEFAULT_TERMS[row.key].singular}
                InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 30 }} disabled={saving} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="Plural" value={form[row.key].plural}
                onChange={handleChange(row.key, 'plural')} placeholder={DEFAULT_TERMS[row.key].plural}
                InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 30 }} disabled={saving} />
            </Grid>
          </React.Fragment>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button size="small" onClick={handleReset} disabled={saving}>Reset to defaults</Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving || !dirty}>
          Save terminology
        </Button>
      </Box>
    </Paper>
  );
}
